import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { BusyDto } from '../../../shared/types';
import type { Api } from './api';
import { Icon } from './icons';
import { dayKey } from './pickers';

// Orari liberi: impegni in agenda delle persone scelte, per fissare un appuntamento senza sovrapposizioni.

const pad = (n: number) => String(n).padStart(2, '0');
const toMin = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const toHm = (m: number) => `${pad(Math.floor(m / 60) % 24)}:${pad(m % 60)}`;
const hmOf = (iso: string) => {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
/** Aggiunge minuti a un orario HH:MM (serve per proporre la fine). */
export const addMin = (t: string, min: number) => toHm(Math.min(toMin(t) + min, 23 * 60 + 59));

/** Impegni delle persone per il mese visibile (più qualche giorno intorno). */
export function useBusy(api: Api, userIds: number[], exceptVisit?: number | null, day?: string) {
  const [month, setMonth] = useState(() => (day || dayKey(new Date())).slice(0, 7) + '-01');
  // Il giorno scelto cambia mese (frecce, calendario): carico quel mese
  useEffect(() => {
    if (day) setMonth(day.slice(0, 7) + '-01');
  }, [day]);
  const [items, setItems] = useState<BusyDto[]>([]);
  const users = [...userIds].sort((a, b) => a - b).join(',');

  useEffect(() => {
    if (!users) {
      setItems([]);
      return;
    }
    const first = new Date(month + 'T00:00:00');
    const from = new Date(first.getFullYear(), first.getMonth(), first.getDate() - 7);
    const to = new Date(first.getFullYear(), first.getMonth() + 1, 14);
    let alive = true;
    const qs = `from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}&users=${users}${exceptVisit ? `&exceptVisit=${exceptVisit}` : ''}`;
    api
      .get<BusyDto[]>(`/api/agenda/busy?${qs}`)
      .then((r) => alive && setItems(r))
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, [api, users, month, exceptVisit]);

  const onDay = useCallback((day: string) => items.filter((b) => dayKey(new Date(b.startsAt)) <= day && dayKey(new Date(b.endsAt)) >= day), [items]);
  const count = useCallback((day: string) => onDay(day).length, [onDay]);
  /** Il calendario mostra un altro mese: carica i suoi impegni. */
  const showMonth = useCallback((firstDay: string) => setMonth(firstDay.slice(0, 7) + '-01'), []);
  return { items, onDay, count, showMonth };
}

const DURATIONS = [
  { m: 30, label: '30 min' },
  { m: 60, label: '1 ora' },
  { m: 90, label: '1 ora e ½' },
  { m: 120, label: '2 ore' },
];

const PARTS = [
  { name: 'Mattina', from: 8 * 60, to: 13 * 60 },
  { name: 'Pomeriggio', from: 13 * 60, to: 18 * 60 },
  { name: 'Sera', from: 18 * 60, to: 20 * 60 },
];

/**
 * Giornata a mezz'ore: libero, occupato (con chi) e l'orario scelto come una barra continua.
 * Nessun orario è scelto finché non si tocca uno spazio libero; la durata si sceglie con i tasti sopra.
 */
export function SlotPicker({
  day,
  busy,
  start,
  end,
  onPick,
  onDay,
  defaultMinutes = 60,
  travel = 0,
}: {
  day: string;
  busy: BusyDto[];
  start: string;
  end: string;
  onPick: (start: string, end: string) => void;
  /** Frecce per il giorno prima / dopo */
  onDay?: (day: string) => void;
  defaultMinutes?: number;
  /** Viaggio di andata e ritorno (minuti): anche quel tempo deve essere libero */
  travel?: number;
}) {
  const has = /^\d{2}:\d{2}$/.test(start);
  const s0 = has ? toMin(start) : -1;
  const e0 = has && end ? toMin(end) : -1;
  const [pref, setPref] = useState(defaultMinutes);
  const duration = has && e0 > s0 ? e0 - s0 : pref;
  const [hover, setHover] = useState<number | null>(null);

  const timed = busy.filter((b) => !b.allDay);
  const allDay = busy.filter((b) => b.allDay);
  const now = Date.now();

  const slots = useMemo(() => {
    const out: { m: number; who: BusyDto[]; past: boolean }[] = [];
    for (let m = PARTS[0]!.from; m < PARTS[PARTS.length - 1]!.to; m += 30) {
      const a = new Date(`${day}T${toHm(m)}:00`).getTime();
      const b = a + 30 * 60_000;
      const pa = a - travel * 60_000;
      const pb = b + travel * 60_000;
      out.push({ m, past: b <= now, who: timed.filter((x) => new Date(x.startsAt).getTime() < pb && new Date(x.endsAt).getTime() > pa) });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, travel, timed.length, timed.map((t) => t.id + t.startsAt).join()]);

  const clash = has
    ? timed.filter((x) => {
        const a = new Date(`${day}T${toHm(s0)}:00`).getTime() - travel * 60_000;
        const b = a + (duration + 2 * travel) * 60_000;
        return new Date(x.startsAt).getTime() < b && new Date(x.endsAt).getTime() > a;
      })
    : [];

  const shiftDay = (n: number) => {
    const d = new Date(day + 'T12:00:00');
    d.setDate(d.getDate() + n);
    onDay?.(dayKey(d));
  };
  const setDuration = (m: number) => {
    setPref(m);
    if (has) onPick(start, toHm(s0 + m));
  };

  const title = new Date(day + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
  const freeCount = slots.filter((s) => !s.who.length && !s.past).length;

  // Anteprima al passaggio del mouse: dove andrebbe l'appuntamento
  const inRange = (m: number, from: number) => from >= 0 && m >= from && m < from + duration;

  return (
    <div className="slots">
      <div className="slots-top">
        <div className="slots-day">
          {onDay && (
            <button type="button" className="slots-nav" onClick={() => shiftDay(-1)} title="Giorno prima">
              <Icon name="chevronLeft" size={16} />
            </button>
          )}
          <div>
            <b>{title}</b>
            <span className="muted small">{freeCount ? `${freeCount} mezz'ore libere` : 'Nessuno spazio libero'}</span>
          </div>
          {onDay && (
            <button type="button" className="slots-nav" onClick={() => shiftDay(1)} title="Giorno dopo">
              <Icon name="chevronRight" size={16} />
            </button>
          )}
        </div>
        <div className="slots-dur" role="group" aria-label="Durata">
          {DURATIONS.map((d) => (
            <button type="button" key={d.m} className={duration === d.m ? 'on' : ''} onClick={() => setDuration(d.m)}>
              {d.label}
            </button>
          ))}
        </div>
      </div>

      {allDay.length > 0 && (
        <div className="slots-allday small">
          <Icon name="calendar" size={13} /> Tutto il giorno: {allDay.map((b) => `${b.userName.split(' ')[0]} · ${b.title}`).join(', ')}
        </div>
      )}

      {PARTS.map((p) => (
        <div className="slots-part" key={p.name}>
          <span className="slots-part-name">{p.name}</span>
          <div className="slots-row" onMouseLeave={() => setHover(null)}>
            {slots
              .filter((s) => s.m >= p.from && s.m < p.to)
              .map((s) => {
                const on = inRange(s.m, s0);
                const first = on && s.m === s0;
                const last = on && s.m + 30 >= s0 + duration;
                const pre = !on && hover !== null && inRange(s.m, hover);
                const names = [...new Set(s.who.map((w) => w.userName.split(' ')[0]))].join(', ');
                const cls = ['slot', s.who.length ? 'busy' : 'free', s.past ? 'past' : '', on ? 'on' : '', first ? 'first' : '', last ? 'last' : '', pre ? 'pre' : '']
                  .filter(Boolean)
                  .join(' ');
                return (
                  <button
                    type="button"
                    key={s.m}
                    className={cls}
                    title={
                      s.who.length
                        ? s.who.map((w) => `${w.userName}: ${w.title} (${hmOf(w.startsAt)}-${hmOf(w.endsAt)})`).join('\n')
                        : `${toHm(s.m)} - ${toHm(s.m + duration)}`
                    }
                    onMouseEnter={() => setHover(s.m)}
                    onClick={() => onPick(toHm(s.m), toHm(s.m + duration))}
                  >
                    <b>{toHm(s.m)}</b>
                    {s.who.length > 0 && <span>{names}</span>}
                  </button>
                );
              })}
          </div>
        </div>
      ))}

      <div className={`slots-foot${!has ? '' : clash.length ? ' bad' : ' good'}`}>
        {!has ? (
          <>
            <Icon name="clock" size={15} /> Tocca un orario libero per fissarlo
          </>
        ) : clash.length ? (
          <>
            <Icon name="x" size={15} /> Si sovrappone a: {clash.map((c) => `${c.userName.split(' ')[0]} · ${c.title} (${hmOf(c.startsAt)}-${hmOf(c.endsAt)})`).join('; ')}
          </>
        ) : (
          <>
            <Icon name="check" size={15} /> {toHm(s0)} - {toHm(s0 + duration)} · libero per tutti
          </>
        )}
      </div>
    </div>
  );
}
